import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useSpring, useInView, animate } from 'framer-motion';
import {
  Heart,
  Users,
  Target,
  ArrowRight,
  Rocket,
  Beaker,
  Briefcase,
  MessageSquare,
  TrendingUp,
} from 'lucide-react';
import VideoModal, { VideoThumbnail } from '../components/VideoModal';

const SPRING_SOFT = { type: 'spring', stiffness: 60, damping: 20 };
const SPRING_CRISP = { type: 'spring', stiffness: 120, damping: 28 };
const EASE_EXPO = [0.16, 1, 0.3, 1];

function ParallaxHero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const opacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.6], [1, 0.92]);
  const springY1 = useSpring(y1, { stiffness: 80, damping: 30 });
  const springY2 = useSpring(y2, { stiffness: 60, damping: 25 });

  return (
    <section ref={ref} className="relative min-h-[90vh] flex items-center justify-center px-6 overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 60% 40% at 50% 30%, rgba(59,130,246,0.07) 0%, transparent 70%)' }}
      />
      <motion.div style={{ opacity, scale }} className="relative z-10 text-center max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE_EXPO }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-500/20 bg-blue-500/5 mb-10"
        >
          <Beaker className="w-3.5 h-3.5 text-blue-400" strokeWidth={1.5} />
          <span className="text-blue-400 text-xs font-medium tracking-widest uppercase">
            Started as a Science Fair Project
          </span>
        </motion.div>

        <div className="overflow-hidden mb-3">
          <motion.h1
            style={{ y: springY1 }}
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE_EXPO, delay: 0.15 }}
            className="text-[clamp(3.5rem,10vw,8rem)] font-bold tracking-tight leading-none text-white"
          >
            The Story Behind
          </motion.h1>
        </div>

        <div className="overflow-hidden mb-10">
          <motion.h1
            initial={{ opacity: 0, y: 80 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE_EXPO, delay: 0.28 }}
            className="text-[clamp(3.5rem,10vw,8rem)] font-bold tracking-tight leading-none"
            style={{
              y: springY2,
              background: 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            ClearWay
          </motion.h1>
        </div>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE_EXPO, delay: 0.45 }}
          className="text-lg text-gray-400 max-w-xl mx-auto leading-relaxed"
        >
          From a 7th grade science fair project in{' '}
          <span className="text-white font-medium">2021</span> to a product concept
          aimed at improving independent mobility.
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.6 }}
          className="mt-16 flex flex-col items-center gap-2"
        >
          <span className="text-[10px] uppercase tracking-[0.25em] text-gray-600">Scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.6, ease: 'easeInOut' }}
            className="w-px h-10 bg-gradient-to-b from-gray-600 to-transparent"
          />
        </motion.div>
      </motion.div>
    </section>
  );
}

function MilestoneCard({ milestone, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const isLeft = index % 2 === 0;
  const colors = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#06b6d4', '#ec4899'];
  const color = colors[index % colors.length];

  return (
    <div
      ref={ref}
      className={`flex flex-col md:flex-row items-center gap-8 ${isLeft ? 'md:flex-row' : 'md:flex-row-reverse'}`}
    >
      <motion.div
        initial={{ opacity: 0, x: isLeft ? -60 : 60, filter: 'blur(8px)' }}
        animate={inView ? { opacity: 1, x: 0, filter: 'blur(0px)' } : {}}
        transition={{ ...SPRING_SOFT, delay: 0.05 }}
        className={`flex-1 ${isLeft ? 'md:text-right' : 'md:text-left'}`}
      >
        <div
          className="inline-block p-[1px] rounded-2xl"
          style={{ background: `linear-gradient(135deg, ${color}30, transparent)` }}
        >
          <div className="p-6 rounded-2xl bg-[#111] relative overflow-hidden">
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-2xl"
              style={{ background: `radial-gradient(ellipse 80% 60% at ${isLeft ? '100%' : '0%'} 0%, ${color}10, transparent)` }}
            />
            <p className="text-xs font-semibold uppercase tracking-[0.2em] mb-2" style={{ color }}>
              {milestone.year}
            </p>
            <h3 className="text-xl font-semibold text-white mb-2">{milestone.title}</h3>
            <p className="text-gray-400 text-sm leading-relaxed">{milestone.description}</p>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={inView ? { scale: 1, opacity: 1 } : {}}
        transition={{ ...SPRING_CRISP, delay: 0.15 }}
        className="relative z-10 flex-shrink-0"
      >
        <div
          className="w-4 h-4 rounded-full ring-4 ring-[#0f0f0f]"
          style={{ background: color, boxShadow: `0 0 16px ${color}80` }}
        />
      </motion.div>

      <div className="flex-1 hidden md:block" />
    </div>
  );
}

function TimelineSection({ milestones }) {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ['start 80%', 'end 20%'] });
  const pathLength = useSpring(scrollYProgress, { stiffness: 40, damping: 20 });

  return (
    <section ref={containerRef} className="py-32 px-6 bg-[#0a0a0a] relative overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 50% 60% at 50% 50%, rgba(99,102,241,0.04) 0%, transparent 70%)' }}
      />
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE_EXPO }}
          className="text-center mb-24"
        >
          <p className="text-[10px] uppercase tracking-[0.3em] text-gray-600 mb-4">Chapter by chapter</p>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">The Journey</h2>
          <p className="text-gray-500 max-w-lg mx-auto">
            From science fair to product — the milestones that shaped ClearWay.
          </p>
        </motion.div>

        <div className="relative">
          <svg
            className="absolute left-1/2 -translate-x-1/2 top-0 h-full w-px hidden md:block overflow-visible"
            style={{ zIndex: 0 }}
          >
            <motion.line
              x1="0" y1="0" x2="0" y2="100%"
              stroke="url(#timelineGrad)"
              strokeWidth="1"
              pathLength="1"
              style={{ pathLength }}
            />
            <defs>
              <linearGradient id="timelineGrad" x1="0" y1="0" x2="0" y2="1" gradientUnits="objectBoundingBox">
                <stop offset="0%" stopColor="#3b82f6" />
                <stop offset="40%" stopColor="#8b5cf6" />
                <stop offset="70%" stopColor="#06b6d4" />
                <stop offset="100%" stopColor="#10b981" />
              </linearGradient>
            </defs>
          </svg>

          <div className="space-y-16 relative z-10">
            {milestones.map((milestone, index) => (
              <MilestoneCard key={milestone.year + milestone.title} milestone={milestone} index={index} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CountStat({ value, label }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  // Start at the true value so the correct number is present in the DOM even
  // before/without the scroll-triggered animation (non-JS crawlers, screen
  // readers, reduced-motion). The count-up is progressive enhancement only.
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    if (!inView || prefersReducedMotion) return;
    const controls = animate(0, value, {
      duration: 1.4,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return controls.stop;
  }, [inView, value, prefersReducedMotion]);

  return (
    <div ref={ref} className="text-center">
      <p className="text-5xl font-bold text-white tabular-nums">{display}+</p>
      <p className="text-gray-500 text-sm mt-2 tracking-wide">{label}</p>
    </div>
  );
}

function LearningCard({ item, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40, filter: 'blur(6px)' }}
      animate={inView ? { opacity: 1, y: 0, filter: 'blur(0px)' } : {}}
      transition={{ duration: 0.7, ease: EASE_EXPO, delay: index * 0.1 }}
      className="group relative"
    >
      <div className="p-[1px] rounded-2xl bg-gradient-to-b from-white/5 to-transparent">
        <div className="p-6 rounded-2xl bg-gradient-to-b from-[#161616] to-[#0f0f0f] h-full relative overflow-hidden transition-all duration-500 group-hover:from-[#1a1a1a]">
          <div
            aria-hidden
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            style={{ background: 'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(59,130,246,0.06), transparent)' }}
          />
          <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-400 mb-5 group-hover:scale-110 transition-transform duration-300">
            {item.icon}
          </div>
          <h3 className="text-base font-semibold text-white mb-2">{item.title}</h3>
          <p className="text-gray-500 text-sm leading-relaxed">{item.description}</p>
        </div>
      </div>
    </motion.div>
  );
}

function FounderSection({ onOpenVideo }) {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ['start end', 'end start'] });
  const cardY = useTransform(scrollYProgress, [0, 1], [30, -30]);
  const cardSpringY = useSpring(cardY, { stiffness: 60, damping: 25 });

  const storyBlocks = [
    {
      label: '01 — Origin',
      color: '#3b82f6',
      title: 'How It Started',
      paragraphs: [
        'ClearWay began in 2021 as a project I built for a school science fair. I wanted to create something that addressed a real-world problem rather than just demonstrate a concept, which led me to explore assistive technologies used by people with visual impairments.',
        'During my research, I learned that while traditional walking canes are essential mobility tools, they only reveal an obstacle at the moment of contact. Low-level obstacles—kerbs, steps, sudden drops, or objects left on the path—are often found too late to react comfortably.',
      ],
      hasVideo: false,
    },
    {
      label: '02 — Build',
      color: '#8b5cf6',
      title: 'Building the First Prototype',
      paragraphs: [
        'This inspired me to begin experimenting with sensors, microcontrollers, and feedback systems to see if a walking stick could provide earlier warnings about obstacles ahead. The first version of ClearWay was a simple prototype that used sensors to detect objects and alert the user through intuitive feedback.',
        'Building it involved a lot of iteration—testing different components, refining how alerts were delivered, and redesigning the hardware to make the system reliable and practical.',
      ],
      hasVideo: true,
    },
    {
      label: '03 — Evolution',
      color: '#10b981',
      title: 'From Project to Product',
      paragraphs: [
        'What started as a science fair project soon became something much more meaningful. Working on ClearWay showed me how small technological improvements can have a real impact when they address everyday accessibility challenges. Since then, the project has continued to evolve from a prototype into a product concept aimed at improving independent mobility.',
      ],
      hasVideo: false,
    },
    {
      label: '04 — Now',
      color: '#f59e0b',
      title: 'Where We Are Now',
      paragraphs: [
        'ClearWay is currently preparing for early testing with users, including students in Dubai and individuals in India. These trials will help refine the design, understand how people interact with the device in real environments, and guide the next stages of development.',
        'What began as a student project has grown into a broader effort to explore how thoughtful engineering and accessible design can make everyday navigation safer and more intuitive.',
      ],
      hasVideo: false,
    },
  ];

  return (
    <section ref={containerRef} className="py-32 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-5 gap-16 items-start">
          <motion.div style={{ y: cardSpringY }} className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: EASE_EXPO }}
              className="sticky top-32"
            >
              <div className="p-[1px] rounded-3xl bg-gradient-to-br from-white/8 via-white/3 to-transparent">
                <div
                  className="p-8 rounded-3xl bg-gradient-to-br from-[#141414] to-[#0c0c0c] relative overflow-hidden"
                  style={{ boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.06)' }}
                >
                  <div
                    aria-hidden
                    className="pointer-events-none absolute top-0 left-0 right-0 h-48"
                    style={{ background: 'radial-gradient(ellipse 100% 80% at 30% 0%, rgba(59,130,246,0.08), transparent)' }}
                  />
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ ...SPRING_CRISP, delay: 0.2 }}
                    className="w-20 h-20 rounded-2xl flex items-center justify-center text-white text-2xl font-bold mb-6 relative"
                    style={{ background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)' }}
                  >
                    JT
                    <div
                      className="absolute inset-0 rounded-2xl"
                      style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 50%)' }}
                    />
                  </motion.div>

                  <h2 className="text-xl font-bold text-white mb-1">Jaiveer Talwar</h2>
                  <p className="text-blue-400 text-sm font-medium mb-4">Creator & Builder</p>
                  <p className="text-gray-500 text-sm leading-relaxed mb-6">
                    Student entrepreneur who started ClearWay in 7th grade as a science fair project.
                    Based between India and UAE.
                  </p>

                  <div className="space-y-3 mb-6">
                    {[
                      { color: '#4ade80', label: 'Hardware & Software Engineering' },
                      { color: '#60a5fa', label: 'Sensor Systems & Feedback Design' },
                      { color: '#a78bfa', label: 'Assistive Technology' },
                    ].map(({ color, label }) => (
                      <div key={label} className="flex items-center gap-3 text-sm">
                        <div
                          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{ background: color, boxShadow: `0 0 6px ${color}` }}
                        />
                        <span className="text-gray-400">{label}</span>
                      </div>
                    ))}
                  </div>

                  <div className="pt-5 border-t border-white/5">
                    <p className="text-[10px] uppercase tracking-widest text-gray-600 mb-1">Operated by</p>
                    <p className="text-xs text-gray-400">Clearway Mobility LLP</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          <div className="lg:col-span-3 space-y-16">
            {storyBlocks.map((block, i) => (
              <motion.div
                key={block.label}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.8, ease: EASE_EXPO, delay: 0.05 * i }}
              >
                <p
                  className="text-[10px] uppercase tracking-[0.25em] font-medium mb-3"
                  style={{ color: block.color }}
                >
                  {block.label}
                </p>
                <h3 className="text-2xl font-semibold text-white mb-4">{block.title}</h3>
                {block.paragraphs.map((p, j) => (
                  <p
                    key={j}
                    className={`text-gray-400 leading-relaxed ${j < block.paragraphs.length - 1 ? 'mb-4' : ''}`}
                  >
                    {p}
                  </p>
                ))}
                {block.hasVideo && (
                  <div className="mt-6">
                    <p className="text-xs text-gray-600 mb-3">The original science fair presentation (2021)</p>
                    <VideoThumbnail onClick={onOpenVideo} />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function VisionSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section ref={ref} className="py-32 px-6 bg-[#0a0a0a] relative overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 100%, rgba(59,130,246,0.05), transparent)' }}
      />
      <div className="max-w-4xl mx-auto text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ ...SPRING_CRISP, delay: 0.1 }}
          className="w-14 h-14 rounded-2xl mx-auto mb-10 flex items-center justify-center"
          style={{
            background: 'linear-gradient(135deg, rgba(59,130,246,0.15), rgba(139,92,246,0.15))',
            border: '1px solid rgba(59,130,246,0.2)',
          }}
        >
          <Rocket className="w-6 h-6 text-blue-400" strokeWidth={1.5} />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease: EASE_EXPO, delay: 0.2 }}
          className="text-[10px] uppercase tracking-[0.3em] text-gray-600 mb-4"
        >
          Looking ahead
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: EASE_EXPO, delay: 0.25 }}
          className="text-4xl md:text-5xl font-bold text-white mb-8"
        >
          The Vision
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: EASE_EXPO, delay: 0.35 }}
          className="text-xl text-gray-300 leading-relaxed mb-6"
        >
          ClearWay is more than a prototype—it&apos;s the foundation for a mission-driven venture
          focused on making independent mobility safer, more intuitive, and more accessible.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: EASE_EXPO, delay: 0.42 }}
          className="text-gray-500 leading-relaxed mb-12"
        >
          The goal is to validate the product through real-world testing, build partnerships
          with schools, communities, and mobility organizations, and develop a sustainable path
          to scale—so ClearWay can reach the people who need it most.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: EASE_EXPO, delay: 0.5 }}
        >
          <div
            className="p-[1px] rounded-2xl inline-block"
            style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.3), transparent)' }}
          >
            <div
              className="px-8 py-6 rounded-2xl bg-[#0f0f0f]"
              style={{ boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.04)' }}
            >
              <div className="flex items-start gap-4 text-left">
                <Target className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <p className="text-gray-300 text-sm leading-relaxed">
                  <span className="font-semibold text-white">Mission: </span>
                  Building a scalable, user-first mobility solution through thoughtful design,
                  real-world validation, and strong partnerships.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: EASE_EXPO, delay: 0.6 }}
          className="mt-16 grid grid-cols-3 gap-8 border-t border-white/5 pt-12"
        >
          <CountStat value={4} label="Years of iteration" />
          <CountStat value={3} label="Prototype generations" />
          <CountStat value={2} label="Testing markets" />
        </motion.div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="py-32 px-6">
      <div className="max-w-4xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: EASE_EXPO }}
        >
          <p className="text-[10px] uppercase tracking-[0.3em] text-gray-600 mb-5">Join the mission</p>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Want to be part<br />of this journey?
          </h2>
          <p className="text-gray-500 mb-12 max-w-md mx-auto leading-relaxed">
            Whether you want to try ClearWay, partner with us, or just learn more—
            I&apos;d love to hear from you.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/shop"
              className="group inline-flex items-center gap-3 px-6 py-3.5 rounded-full bg-white text-black font-medium hover:bg-gray-100 transition-colors duration-300"
            >
              Get ClearWay
              <span className="w-7 h-7 rounded-full bg-black/10 flex items-center justify-center group-hover:translate-x-0.5 group-hover:-translate-y-px transition-transform duration-300">
                <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
            <Link
              to="/impact"
              className="group inline-flex items-center gap-3 px-6 py-3.5 rounded-full border border-white/10 text-white font-medium hover:border-white/20 hover:bg-white/5 transition-all duration-300"
            >
              See Our Impact
              <span className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Heart className="w-3.5 h-3.5 text-rose-400" strokeWidth={1.5} />
              </span>
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-gray-400 hover:text-white transition-colors duration-300 text-sm"
            >
              Get in Touch →
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

const journeyMilestones = [
  { year: '2021', title: '7th Grade Science Fair', description: 'First prototype built as a school project — the spark that started everything.' },
  { year: '2022', title: 'Continued Development', description: 'Refined sensors and feedback systems through hands-on experimentation.' },
  { year: '2023', title: 'Research & Testing', description: 'User studies and prototype iterations grounded the design in real needs.' },
  { year: '2024', title: 'Product Development', description: 'Evolved from prototype to product concept with a clear value proposition.' },
  {
    year: '2025',
    title: 'Internship at Exactech UAE',
    description: 'Completed an internship at a leading prosthetics firm, gaining real-world exposure to mobility challenges and assistive-tech ecosystems.',
  },
  { year: '2025', title: 'Early Testing', description: 'Preparing trials with users in Dubai and India to validate the design in real environments.' },
  {
    year: 'April 2026',
    title: 'First trial at a school for the blind',
    description: 'ClearWay completed its first formal trial at Kamla Mehta School for the Blind, Mumbai — testing across hallways, staircases, and outdoor grounds with totally blind participants. Feedback was understood immediately, with no training needed, and every participant wanted to continue using ClearWay. "This is going to give us real independence," one student said.',
  },
];

const learnings = [
  {
    icon: <Users className="w-5 h-5" strokeWidth={1.5} />,
    title: 'Customer Discovery',
    description: 'Learned to listen deeply, validate assumptions, and understand the real "job to be done" before building features.',
  },
  {
    icon: <TrendingUp className="w-5 h-5" strokeWidth={1.5} />,
    title: 'Product-Market Fit Thinking',
    description: 'Practiced defining a clear value proposition, identifying early adopters, and refining the concept based on feedback.',
  },
  {
    icon: <MessageSquare className="w-5 h-5" strokeWidth={1.5} />,
    title: 'Communication & Storytelling',
    description: 'Got better at pitching, writing clearly, and explaining the "why" in a way that earns trust from users and partners.',
  },
  {
    icon: <Briefcase className="w-5 h-5" strokeWidth={1.5} />,
    title: 'Execution & Partnerships',
    description: 'Learned how progress comes from consistent execution—plus building the right relationships to iterate and scale.',
  },
];

const AboutPage = () => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  return (
    <div className="pt-24 bg-[#0a0a0a]">
      <ParallaxHero />
      <FounderSection onOpenVideo={() => setIsVideoOpen(true)} />
      <TimelineSection milestones={journeyMilestones} />

      <section className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE_EXPO }}
            className="mb-16"
          >
            <p className="text-[10px] uppercase tracking-[0.3em] text-gray-600 mb-4">Lessons</p>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
                What Building ClearWay<br />Taught Me
              </h2>
              <p className="text-gray-500 max-w-xs text-sm leading-relaxed">
                Beyond the technical skills — the lessons from creating something real.
              </p>
            </div>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {learnings.map((item, index) => (
              <LearningCard key={item.title} item={item} index={index} />
            ))}
          </div>
        </div>
      </section>

      <VisionSection />
      <CTASection />

      <VideoModal
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        videoUrl="https://videos.jtlr.org/v/zeRuiOfYxpH01X5FdXevirD00Id02Yc2oNl1w3Cd5hb3Ic/embed"
      />
    </div>
  );
};

export default AboutPage;
